import { TitleCasePipe } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  Component,
  computed,
  forwardRef,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { FormValueControl } from '@angular/forms/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { castArray, isEqual, isNaN, round } from 'lodash';
import { ONYX_FOCUSABLE } from '../../../directives/onyx-auto-focus.directive';
import { OnyxTooltipDirective } from '../../../directives/onyx-tooltip.directive';
import type { OnyxMaybeArray, OnyxOptional } from '../../../interfaces';
import { OnyxBaseFormControlComponent } from '../../../internal/components/onyx-base-form-control/onyx-base-form-control.component';
import { INTEGER_REGEXP } from '../../../internal/constants';
import { OnyxClearButtonComponent } from '../../buttons/onyx-clear-button/onyx-clear-button.component';
import { OnyxIconButtonComponent } from '../../buttons/onyx-icon-button/onyx-icon-button.component';
import { OnyxErrorComponent } from '../../error/onyx-error/onyx-error.component';
import { OnyxIconComponent } from '../../icons/onyx-icon/onyx-icon.component';
import type { OnyxLabelLink } from '../../labels/interfaces';
import { OnyxInputLabelComponent } from '../../labels/onyx-input-label/onyx-input-label.component';
import type { OnyxTooltipContext } from '../../tooltip/interfaces';
import { OnyxTextTransform } from '../enums';

@Component({
  selector: 'onyx-text-field',
  imports: [
    OnyxErrorComponent,
    OnyxClearButtonComponent,
    OnyxInputLabelComponent,
    OnyxErrorComponent,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    TranslatePipe,
    OnyxTooltipDirective,
  ],
  providers: [
    TitleCasePipe,
    {
      provide: ONYX_FOCUSABLE,
      useExisting: forwardRef(() => OnyxTextFieldComponent),
    },
  ],
  templateUrl: './onyx-text-field.component.html',
  styleUrls: ['./onyx-text-field.component.scss'],
  host: { '[style.width]': `width()` },
})
export class OnyxTextFieldComponent<T>
  extends OnyxBaseFormControlComponent
  implements FormValueControl<T | null>
{
  private readonly titleCasePipe = inject(TitleCasePipe);

  public readonly value = model<T | null>(null);
  public readonly size = input<32 | 40>(40);
  public readonly inputType = input<'text' | 'email' | 'password' | 'url'>(
    'text',
  );
  public readonly label = input<string>();
  public readonly hint = input<OnyxTooltipContext>();
  public readonly width = input('100%');
  public readonly autocomplete = input<string>();
  public readonly placeholder = input<string>();
  public readonly decimalPlaces = input<number | null>(null);
  public readonly unit = input<OnyxOptional<string>>();
  public readonly acceptNegative = input(false);
  public readonly link = input<OnyxLabelLink | null>(null);
  public readonly alignLabelRight = input(false);
  public readonly maxLength = input<number>();
  public readonly textTransform =
    input<OnyxMaybeArray<`${OnyxTextTransform}`>>();
  public readonly showRequired = input(true);
  public readonly showErrors = input(true);
  public readonly warning = input<OnyxOptional<string>>();
  public readonly showClear = input(true);
  public readonly showPassword = model<boolean | null>(null);

  protected value_ = signal<T | null>(null);
  protected hasError = signal(false);
  protected focused = signal(false);
  protected warningVisible = linkedSignal({
    source: this.warning,
    computation: () => !untracked(this.focused),
  });
  protected computedType = computed(() => {
    if (this.showPassword() == null) return this.inputType();
    return this.showPassword() ? 'text' : 'password';
  });
  protected maxLengthHint = computed(() => {
    if (this.maxLength() == null) return undefined;
    return `${this.value_()?.toString()?.length ?? 0}/${this.maxLength()}`;
  });
  protected isInteger = computed<boolean | null>(() => {
    if (this.decimalPlaces() == null) return null;
    return this.decimalPlaces() === 0;
  });
  protected customPattern = computed(() => {
    if (this.isInteger()) return INTEGER_REGEXP;

    const places = this.decimalPlaces() ?? 2;
    const pattern = `^-?\\d*(?:[,.]\\d{0,${places}})?$`;
    return new RegExp(pattern);
  });

  private inputElementRef =
    viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  private previousValue = signal<T | null>(null);

  public override ngOnInit(): void {
    super.ngOnInit();

    toObservable(this.value, { injector: this.injector })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (isEqual(value, this.previousValue())) return;

        this.previousValue.set(value);
        this.updateDisplay(value);
      });
  }

  public focus(): void {
    if (this.disabled()) return;
    this.inputElementRef().nativeElement.focus();
  }

  protected validateInput(event: KeyboardEvent): boolean {
    if (this.decimalPlaces() == null) return true;

    const target = event.target as HTMLInputElement;
    const { value, selectionStart, selectionEnd } = target;

    if (event.key === '-') {
      if (!this.acceptNegative()) {
        event.preventDefault();
        return false;
      }

      const newValue = this.toggleNegativeSign(value);
      const numberValue = Number(newValue) as T;

      if (!isNaN(numberValue)) {
        const value = this.isInteger()
          ? numberValue
          : this.convertNumber(numberValue as number, 'integer');

        this.value_.set(numberValue);
        this.setValue(value);
      }

      this.setInputValue(newValue);
      event.preventDefault();
      return false;
    }

    const newValue =
      value.slice(0, selectionStart!) + event.key + value.slice(selectionEnd!);
    if (!this.customPattern().test(newValue)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  protected validatePaste(event: ClipboardEvent): boolean {
    if (this.decimalPlaces() == null) return true;

    const target = event.target as HTMLInputElement;
    const { value, selectionStart, selectionEnd } = target;
    const pasteData = event.clipboardData?.getData('text') ?? '';

    const newValue = this.transformValue(
      value.slice(0, selectionStart!) + pasteData + value.slice(selectionEnd!),
    );

    if (this.customPattern().test(newValue)) {
      const numberValue = Number(newValue) as T;
      const value = this.isInteger()
        ? numberValue
        : this.convertNumber(numberValue as number, 'integer');

      if (value != null && (value as number) < 0 && !this.acceptNegative()) {
        event.preventDefault();
        return false;
      }

      this.setValue(value);
      this.setInputValue(newValue);
    }

    event.preventDefault();
    return false;
  }

  protected clearInput(): void {
    this.value_.set(null);
    this.setValue(null);
    this.setInputValue('', true);
  }

  protected toggleShowPassword(): void {
    this.showPassword.update((showPassword) => !showPassword);
  }

  protected trimValue(): void {
    const value = this.value_();
    if (!value || this.decimalPlaces() != null) return;

    const trimmedValue = String(value).trim() as T;
    this.value_.set(trimmedValue);
    this.setValue(trimmedValue);
    this.setInputValue(trimmedValue as string, true);
  }

  protected handleValueChange(event: Event): void {
    this.warningVisible.set(false);
    const target = event.target as HTMLInputElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    let value = target.value;
    const initialLength = value.length;

    value = this.transformValue(value);
    this.value_.set(value as T);

    if (this.decimalPlaces() != null) {
      if (!value) {
        this.setValue(null);
        return;
      } else if (this.customPattern().test(value)) {
        const numberValue = Number(value) as T;
        const convertedValue = this.isInteger()
          ? numberValue
          : this.convertNumber(numberValue as number, 'integer');

        this.setValue(convertedValue);
      }
    } else {
      this.setValue((value || null) as T | null);
    }

    const inputElement = this.inputElementRef().nativeElement;
    inputElement.value = value;

    if (start != null && end != null) {
      const lengthDiff = value.length - initialLength;
      inputElement.setSelectionRange(start + lengthDiff, end + lengthDiff);
    }
  }

  private updateDisplay(value: T | null): void {
    const display =
      this.decimalPlaces() != null && !this.isInteger()
        ? this.convertNumber(value as number, 'float')
        : value;

    if (display == null) {
      this.value_.set(null);
      this.setInputValue('', true);
      return;
    }

    if (this.decimalPlaces() != null) {
      if (!this.customPattern().test(display.toString())) return;
    }

    this.value_.set(display);
    this.setInputValue(display.toString(), true);
  }

  private setValue(value: T | null): void {
    this.previousValue.set(value);
    this.value.set(value);
  }

  private setInputValue(value: string, deferred = false): void {
    if (deferred) {
      setTimeout(() => this.setInputValue(value));
      return;
    }

    this.inputElementRef().nativeElement.value = value;
  }

  private toggleNegativeSign(value: string): string {
    if (!this.acceptNegative()) return value;

    return value.startsWith('-') ? value.slice(1) : `-${value}`;
  }

  private convertNumber(
    value: number | null,
    format: 'integer' | 'float',
  ): T | null {
    if (value == null) return null;

    const decimalPlaces = this.decimalPlaces()!;
    if (format === 'integer') {
      return Math.round(value * 10 ** decimalPlaces) as T;
    }
    return round(value / 10 ** decimalPlaces, decimalPlaces) as T;
  }

  private transformValue(value: string): string {
    if (this.decimalPlaces() != null) {
      return value
        .replace(',', '.')
        .replace(/^0(?=[0-9])/, '')
        .trim();
    }

    value = value.slice(0, this.maxLength());

    return castArray(this.textTransform()).reduce(
      (result, transform) => this.applyTransform(result, transform),
      value,
    );
  }

  private applyTransform(
    value: string,
    transform: `${OnyxTextTransform}`,
  ): string {
    switch (transform) {
      case OnyxTextTransform.LOWERCASE:
        return value.toLowerCase();
      case OnyxTextTransform.UPPERCASE:
        return value.toUpperCase();
      case OnyxTextTransform.TITLECASE:
        return value
          .split('-')
          .map((part) => this.titleCasePipe.transform(part))
          .join('-');
      case OnyxTextTransform.REMOVE_WHITESPACES:
        return value.replace(/\s+/g, '');

      default:
        return value;
    }
  }
}
